import { useMemo } from 'react'
import { useAccountType } from './useAccountType'
import { FREE_TIER_LIMITS } from '../utils/constants'

interface UseTestRestrictionsResult {
  isTestLocked: boolean
  isAllowedTest: boolean
  allowedTestCodes: string[]
}

/**
 * Hook to check if a test is locked for free users
 * @param testCode - The test code to check
 */
export function useTestRestrictions(testCode: string): UseTestRestrictionsResult {
  const { isFreeUser } = useAccountType()

  return useMemo(() => {
    const allowedTestCodes = FREE_TIER_LIMITS.allowedTestCodes

    if (!isFreeUser) {
      // Paid users have access to all tests
      return {
        isTestLocked: false,
        isAllowedTest: true,
        allowedTestCodes,
      }
    }

    // Free users can only access specific diagnostic tests
    const isAllowedTest = allowedTestCodes.some(
      (allowedCode) => testCode.toUpperCase().includes(allowedCode.toUpperCase())
    )

    return {
      isTestLocked: !isAllowedTest,
      isAllowedTest,
      allowedTestCodes,
    }
  }, [isFreeUser, testCode])
}

/**
 * Hook to check multiple tests at once
 * Useful for test series pages where we need to check many tests
 */
export function useTestRestrictionsMultiple(testCodes: string[]): Map<string, boolean> {
  const { isFreeUser } = useAccountType()

  return useMemo(() => {
    const allowedTestCodes = FREE_TIER_LIMITS.allowedTestCodes
    const result = new Map<string, boolean>()

    for (const testCode of testCodes) {
      if (!isFreeUser) {
        result.set(testCode, false) // Not locked for paid users
      } else {
        const isAllowed = allowedTestCodes.some(
          (allowedCode) => testCode.toUpperCase().includes(allowedCode.toUpperCase())
        )
        result.set(testCode, !isAllowed) // Locked if not in allowed list
      }
    }

    return result
  }, [isFreeUser, testCodes])
}
